const ECLIPSE_REEL_SELECTOR = '[data-ec-reel]';

let eclipseRetryArmed = false;

/** Un solo gesto del visitante basta para desbloquear la reproducción en iOS. */
function armRetryOnInteraction() {
  if (eclipseRetryArmed) return;
  eclipseRetryArmed = true;

  const retry = () => {
    document.querySelectorAll(`${ECLIPSE_REEL_SELECTOR}[data-ec-autoplay]`).forEach((video) => {
      if (video.dataset.ecInView !== 'false') playWhenReady(video);
    });
  };

  ['touchstart', 'pointerdown', 'click'].forEach((type) => {
    document.addEventListener(type, retry, { once: true, passive: true });
  });
}

function playWhenReady(video) {
  if (video.dataset.ecPlayPending === 'true' || !video.paused) return;
  video.dataset.ecPlayPending = 'true';

  const container = video.closest('[data-ec-reel-container]');

  const start = () => {
    delete video.dataset.ecPlayPending;
    video.defaultMuted = true;
    video.muted = true;
    video
      .play()
      .then(() => {
        if (container) delete container.dataset.ecReelFailed;
      })
      .catch(() => {
        // iOS puede rechazar el autoplay aunque el video esté silenciado.
        // Marcamos el contenedor para mostrar la portada en vez del control nativo.
        if (container) container.dataset.ecReelFailed = 'true';
        armRetryOnInteraction(video);
      });
  };

  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    video.addEventListener('loadeddata', start, { once: true });
    video.load();
    return;
  }

  start();
}

/**
 * La portada se pide recién al acercarse a la pantalla. Con el atributo `poster`
 * escrito desde Liquid, el navegador la descarga siempre, aunque el video esté a
 * tres pantallas de distancia. Va aparte del autoplay para que también funcione
 * con "reducir movimiento" o ahorro de datos, donde el reel no se reproduce.
 */
function armPoster(video) {
  if (!video.dataset.poster) return;

  const apply = () => {
    if (!video.dataset.poster) return;
    video.poster = video.dataset.poster;
    delete video.dataset.poster;
  };

  if (!('IntersectionObserver' in window)) {
    apply();
    return;
  }

  const watcher = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    apply();
    watcher.disconnect();
  }, { rootMargin: '300px 0px' });

  watcher.observe(video);
}

function setupEclipseReel(video) {
  if (video.dataset.ecReelReady === 'true') return;
  video.dataset.ecReelReady = 'true';

  armPoster(video);

  const container = video.closest('[data-ec-reel-container]');
  const isMobileHero = container?.dataset.ecReelMode === 'mobile-hero';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection?.saveData === true;

  video.defaultMuted = true;
  video.muted = true;

  video.addEventListener('ended', () => {
    if (!video.loop) return;
    video.currentTime = 0;
    playWhenReady(video);
  });
  if (!video.hasAttribute('data-ec-autoplay')) return;
  // El reel del hero era solo de celular y aquí se cortaba en escritorio.
  // Ahora también ocupa el lado derecho del hero grande, así que se deja pasar.
  if (!isMobileHero && (reducedMotion || saveData)) return;

  if (isMobileHero && !document.hidden) playWhenReady(video);

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const inView = entry.isIntersecting && entry.intersectionRatio >= 0.2;

      video.dataset.ecInView = inView ? 'true' : 'false';
      if (inView && !document.hidden) playWhenReady(video);
      else if (!inView && !video.paused) video.pause();
    });
  }, { threshold: [0, 0.2, 0.7] });

  observer.observe(video);
}

function setupEclipseReels(root = document) {
  root.querySelectorAll(ECLIPSE_REEL_SELECTOR).forEach(setupEclipseReel);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupEclipseReels());
else setupEclipseReels();

document.addEventListener('shopify:section:load', (event) => setupEclipseReels(event.target));
document.addEventListener('visibilitychange', () => {
  document.querySelectorAll(ECLIPSE_REEL_SELECTOR).forEach((video) => {
    if (document.hidden) video.pause();
    else if (video.dataset.ecInView === 'true') playWhenReady(video);
  });
});
window.addEventListener('pageshow', () => {
  document.querySelectorAll(`${ECLIPSE_REEL_SELECTOR}[data-ec-autoplay]`).forEach((video) => {
    if (video.dataset.ecInView !== 'false') playWhenReady(video);
  });
});

/**
 * Galería del producto: el video que sale de pantalla se calla.
 *
 * El carrusel deja las demás diapositivas montadas a los lados. Sin esto, al
 * pasar al siguiente video el anterior seguía sonando fuera de la vista: se
 * oía una explicación mientras se miraba la foto fija de otra. Parecía que el
 * video «no se reproducía» cuando en realidad sonaba el de al lado.
 */
(() => {
  const CONTENEDOR = '.product-media-container--video';

  const vigilar = () => {
    const contenedores = document.querySelectorAll(CONTENEDOR);
    if (!contenedores.length || typeof IntersectionObserver !== 'function') return;

    const observador = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((entrada) => {
          if (entrada.isIntersecting) return;
          const video = entrada.target.querySelector('video');
          if (!video || video.paused) return;
          video.pause();
          // Silenciado otra vez: si vuelve, empieza como empezó.
          video.muted = true;
        });
      },
      { threshold: 0.5 }
    );

    contenedores.forEach((c) => observador.observe(c));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', vigilar);
  } else {
    vigilar();
  }
  document.addEventListener('shopify:section:load', vigilar);
})();

/**
 * Galería del producto: al tocarlo, que suene.
 *
 * Todo ocurre DENTRO del mismo toque, sin temporizadores. El intento anterior
 * quitaba el silencio desde un `setTimeout` y en iOS eso pierde el contexto
 * del gesto: el sistema dejaba pasar el audio pero suspendía la capa de video,
 * así que se oía la explicación sobre una imagen congelada.
 *
 * El <video> ya existe cuando llega el toque (el reproductor diferido lo monta
 * al entrar la diapositiva en pantalla), así que no hay nada que esperar.
 *
 * Fase de burbuja a propósito: para entonces el reproductor del tema ya hizo
 * lo suyo y no le pisamos el trabajo.
 */
(() => {
  document.addEventListener(
    'click',
    (evento) => {
      const contenedor = evento.target.closest('.product-media-container--video');
      if (!contenedor) return;

      const video = contenedor.querySelector('video');
      if (!video) return;

      // Red de seguridad: el video de la ficha se monta ya con sonido, dentro
      // del mismo gesto. Si aun así el navegador se niega a arrancarlo (modo
      // ahorro, ajuste del sitio), se queda parado enseñando los controles.
      // Antes que dejarlo muerto, lo silenciamos y lo echamos a andar: se ve,
      // y el botón de volumen de los controles nativos sigue ahí.
      if (!video.muted) {
        if (video.paused) {
          setTimeout(() => {
            if (!video.paused) return;
            video.muted = true;
            const reintento = video.play();
            if (reintento) reintento.catch(() => {});
          }, 300);
        }
        return;
      }

      video.muted = false;
      video.volume = 1;
      if (video.paused) {
        const reproducido = video.play();
        if (reproducido) reproducido.catch(() => {});
      }
    },
    false
  );
})();
